import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../hooks/useLanguage';

const PAGE_SIZE = 10;
const QUARTILES = ['Q1', 'Q2', 'Q3', 'Q4'];

const INITIAL_FORM = {
    titulo: '',
    resumen: '',
    cuartiles: [],
    excluir_beall: true,
    tipo_acceso: 'ambos',
    apc_max: null,
    apc_preset: '',
    sjr_min: null,
    areas_tematicas: []
};

const JournalFinderPage = () => {
    const { token } = useAuth();
    const { t, currentLanguage } = useLanguage();
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [resultados, setResultados] = useState([]);
    const [idsEncontrados, setIdsEncontrados] = useState([]);
    const [orderBy, setOrderBy] = useState('sjr');
    const [orderDir, setOrderDir] = useState('desc');
    const [error, setError] = useState(null);
    const [requestMessage, setRequestMessage] = useState(null);
    const [areasTematicas, setAreasTematicas] = useState([]);
    const [areasLoading, setAreasLoading] = useState(false);
    const [areasError, setAreasError] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [expandedResultId, setExpandedResultId] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const requestControllerRef = useRef(null);
    const requestSequenceRef = useRef(0);
    const resultsSectionRef = useRef(null);
    const resultsHeadingRef = useRef(null);
    const focusResultsRef = useRef(false);

    const currentLocale = (currentLanguage || 'es').split('-')[0];
    const requestLanguage = currentLocale === 'en' ? 'en' : 'es';
    const busy = loading || loadingMore;
    const hasInput = Boolean(formData.titulo.trim() || formData.resumen.trim());

    const apcPresets = [
        { value: '', label: t('journalFinder.filters.noLimit') },
        { value: '0', label: t('journalFinder.filters.free') },
        { value: '500', label: t('journalFinder.filters.under500') },
        { value: '1500', label: t('journalFinder.filters.under1500') },
        { value: '3000', label: t('journalFinder.filters.under3000') },
        { value: 'custom', label: t('journalFinder.filters.custom') }
    ];

    const activeFilterCount = [
        formData.cuartiles.length > 0,
        !formData.excluir_beall,
        formData.tipo_acceso !== 'ambos',
        formData.apc_max !== null,
        formData.sjr_min !== null && formData.sjr_min !== '',
        formData.areas_tematicas.length > 0
    ].filter(Boolean).length;

    useEffect(() => {
        if (!token) return undefined;

        const controller = new AbortController();
        const fetchAreas = async () => {
            setAreasLoading(true);
            setAreasError(null);

            try {
                const apiBase = process.env.REACT_APP_API_BASE_URL || '';
                const response = await fetch(`${apiBase}/api/areas-tematicas`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error(t('journalFinder.errors.areasLoadFailed'));
                }

                const data = await response.json();
                setAreasTematicas(data.areas || []);
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Error cargando áreas temáticas:', err);
                    setAreasError(t('journalFinder.errors.areasLoadFailed'));
                }
            } finally {
                if (!controller.signal.aborted) setAreasLoading(false);
            }
        };

        fetchAreas();
        return () => controller.abort();
    }, [token, t]);

    useEffect(() => () => {
        requestControllerRef.current?.abort();
    }, []);

    useEffect(() => {
        if (!loading && !loadingMore && hasSearched && focusResultsRef.current) {
            focusResultsRef.current = false;
            resultsHeadingRef.current?.focus({ preventScroll: true });
        }
    }, [hasSearched, loading, loadingMore]);

    const clearValidationError = useCallback((name) => {
        setValidationErrors(prev => {
            if (!prev[name] && !prev.form) return prev;
            const next = { ...prev };
            delete next[name];
            delete next.form;
            return next;
        });
    }, []);

    const handleChange = useCallback((event) => {
        const { name, value, checked } = event.target;
        clearValidationError(name);

        setFormData(prev => {
            if (name === 'excluir_beall') {
                return { ...prev, excluir_beall: checked };
            }

            if (name === 'apc_preset') {
                return {
                    ...prev,
                    apc_preset: value,
                    apc_max: value === '' || value === 'custom' ? (value === '' ? null : prev.apc_max) : Number(value)
                };
            }

            if (name === 'apc_max' || name === 'sjr_min') {
                return { ...prev, [name]: value === '' ? null : Number(value) };
            }

            return { ...prev, [name]: value };
        });
    }, [clearValidationError]);

    const handleCuartilToggle = useCallback((cuartil) => {
        setFormData(prev => ({
            ...prev,
            cuartiles: prev.cuartiles.includes(cuartil)
                ? prev.cuartiles.filter(value => value !== cuartil)
                : [...prev.cuartiles, cuartil]
        }));
    }, []);

    const handleAreaToggle = useCallback((areaId) => {
        setFormData(prev => ({
            ...prev,
            areas_tematicas: prev.areas_tematicas.includes(areaId)
                ? prev.areas_tematicas.filter(id => id !== areaId)
                : [...prev.areas_tematicas, areaId]
        }));
    }, []);

    const validateForm = useCallback(() => {
        const titulo = formData.titulo.trim();
        const resumen = formData.resumen.trim();
        const nextErrors = {};

        if (!titulo && !resumen) {
            nextErrors.form = t('journalFinder.search.validationError');
        }
        if (titulo && titulo.length < 3) {
            nextErrors.titulo = t('journalFinder.search.titleMinLength');
        }
        if (resumen && resumen.length < 10) {
            nextErrors.resumen = t('journalFinder.search.abstractMinLength');
        }

        setValidationErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [formData.resumen, formData.titulo, t]);

    const formatApiError = useCallback(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (response.status === 422) return t('journalFinder.errors.invalidSearch');
        if (response.status === 403) return t('journalFinder.errors.unauthorized');
        if (response.status >= 500) return t('journalFinder.errors.searchFailed');
        return data.detail || t('journalFinder.errors.searchFailed');
    }, [t]);

    const handleSubmit = async (event, loadMore = false, customOrderBy = null, customOrderDir = null) => {
        event?.preventDefault();

        if (!loadMore && !validateForm()) return;
        if (loadMore && !resultados.length) return;

        requestControllerRef.current?.abort();
        const controller = new AbortController();
        requestControllerRef.current = controller;
        const requestId = requestSequenceRef.current + 1;
        requestSequenceRef.current = requestId;
        const orderByToUse = customOrderBy || orderBy;
        const orderDirToUse = customOrderDir || orderDir;

        if (loadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
            setHasSearched(true);
            setResultados([]);
            setIdsEncontrados([]);
            setExpandedResultId(null);
            setRequestMessage(null);
            focusResultsRef.current = true;
        }
        setError(null);

        try {
            const payload = {
                titulo: formData.titulo.trim() || null,
                resumen: formData.resumen.trim() || null,
                cuartiles: formData.cuartiles.length ? formData.cuartiles : null,
                excluir_beall: formData.excluir_beall,
                tipo_acceso: formData.tipo_acceso === 'ambos' ? null : formData.tipo_acceso,
                apc_max: formData.apc_max === null || formData.apc_max === '' ? null : formData.apc_max,
                sjr_min: formData.sjr_min === null || formData.sjr_min === '' ? null : formData.sjr_min,
                areas_tematicas: formData.areas_tematicas.length ? formData.areas_tematicas : null,
                language: requestLanguage
            };

            const apiBase = process.env.REACT_APP_API_BASE_URL || '';
            let url = `${apiBase}/api/find-journals?limit=${PAGE_SIZE}&order_by=${orderByToUse}&order_dir=${orderDirToUse}`;
            if (loadMore && idsEncontrados.length) {
                url += `&excluir_ids=${idsEncontrados.join(',')}`;
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload),
                signal: controller.signal
            });

            if (!response.ok) throw new Error(await formatApiError(response));
            const data = await response.json();
            if (requestId !== requestSequenceRef.current) return;

            const nuevosResultados = data.resultados || [];
            if (loadMore) {
                setResultados(prev => [...prev, ...nuevosResultados]);
                setIdsEncontrados(prev => [...new Set([...prev, ...nuevosResultados.map(result => result.id)])]);
            } else {
                setResultados(nuevosResultados);
                setIdsEncontrados(nuevosResultados.map(result => result.id));
            }

            setHasMore(typeof data.has_more === 'boolean' ? data.has_more : nuevosResultados.length === PAGE_SIZE);
            setRequestMessage(data.mensaje || null);
        } catch (err) {
            if (err.name === 'AbortError' || requestId !== requestSequenceRef.current) return;
            console.error('Error en búsqueda:', err);
            setError(err.message || t('journalFinder.errors.searchFailed'));
        } finally {
            if (requestId === requestSequenceRef.current) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    };

    const handleReset = () => {
        requestControllerRef.current?.abort();
        requestSequenceRef.current += 1;
        setFormData({ ...INITIAL_FORM });
        setResultados([]);
        setIdsEncontrados([]);
        setHasSearched(false);
        setHasMore(false);
        setError(null);
        setRequestMessage(null);
        setValidationErrors({});
        setExpandedResultId(null);
        setOrderBy('sjr');
        setOrderDir('desc');
        setShowFilters(false);
        setLoading(false);
        setLoadingMore(false);
    };

    const handleOrderChange = (newOrderBy, newOrderDir = orderDir) => {
        setOrderBy(newOrderBy);
        setOrderDir(newOrderDir);
        if (resultados.length && !busy) {
            setIdsEncontrados([]);
            setHasMore(true);
            handleSubmit(null, false, newOrderBy, newOrderDir);
        }
    };

    const isYes = value => value === 'Sí' || value === 'Yes';
    const formatApc = value => {
        if (!value) return value;
        const normalized = String(value).trim();
        const match = normalized.match(/^([\d.,]+)\s*([A-Z]{3})/i);
        if (!match) return normalized;
        const amount = Number(match[1].replace(/,/g, ''));
        if (Number.isNaN(amount)) return normalized;
        const locale = currentLocale === 'es' ? 'es-ES' : 'en-US';
        return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(amount)} ${match[2].toUpperCase()}`;
    };

    return (
        <main className="journal-finder-page">
            <header className="journal-hero">
                <div className="journal-hero__copy">
                    <h1>{t('journalFinder.title')}</h1>
                    <p>{t('journalFinder.subtitle')}</p>
                </div>
                <div className="journal-hero__signal">
                    <i className="bi bi-journal-check" aria-hidden="true"></i>
                    <span>{t('journalFinder.heroNote')}</span>
                </div>
            </header>

            <div className="journal-workspace">
                <aside className={`journal-filter-panel ${showFilters ? 'is-filter-open' : ''}`} aria-labelledby="journal-search-title">
                    <div className="journal-panel-heading">
                        <div>
                            <p className="journal-section-label">{t('journalFinder.search.sectionLabel')}</p>
                            <h2 id="journal-search-title">{t('journalFinder.search.searchTitle')}</h2>
                        </div>
                    </div>
                    <p className="journal-panel-intro">{t('journalFinder.search.searchIntro')}</p>
                    <p className="journal-required-note"><i className="bi bi-info-circle" aria-hidden="true"></i>{t('journalFinder.search.requiredHint')}</p>

                    <form onSubmit={handleSubmit} noValidate>
                        <div className="journal-field-group">
                            <label htmlFor="titulo" className="journal-field-label">
                                {t('journalFinder.search.articleTitle')}
                            </label>
                            <input
                                type="text"
                                className={`form-control journal-input ${validationErrors.titulo ? 'is-invalid' : ''}`}
                                id="titulo"
                                name="titulo"
                                value={formData.titulo}
                                onChange={handleChange}
                                placeholder={t('journalFinder.search.articleTitlePlaceholder')}
                                aria-invalid={Boolean(validationErrors.titulo)}
                                aria-describedby="titulo-hint titulo-count titulo-error"
                                maxLength="500"
                            />
                            <div className="journal-field-meta">
                                <small id="titulo-hint">{t('journalFinder.search.articleTitleHint')}</small>
                                <small id="titulo-count">{formData.titulo.length}/500</small>
                            </div>
                            {validationErrors.titulo && <div id="titulo-error" className="invalid-feedback d-block">{validationErrors.titulo}</div>}
                        </div>

                        <div className="journal-field-group journal-field-group--abstract">
                            <label htmlFor="resumen" className="journal-field-label">
                                {t('journalFinder.search.articleAbstract')}
                            </label>
                            <textarea
                                className={`form-control journal-input journal-textarea ${validationErrors.resumen ? 'is-invalid' : ''}`}
                                id="resumen"
                                name="resumen"
                                value={formData.resumen}
                                onChange={handleChange}
                                rows="7"
                                placeholder={t('journalFinder.search.articleAbstractPlaceholder')}
                                aria-invalid={Boolean(validationErrors.resumen)}
                                aria-describedby="resumen-hint resumen-count resumen-error"
                                maxLength="5000"
                            />
                            <div className="journal-field-meta">
                                <small id="resumen-hint">{t('journalFinder.search.articleAbstractHint')}</small>
                                <small id="resumen-count">{formData.resumen.length}/5000</small>
                            </div>
                            {validationErrors.resumen && <div id="resumen-error" className="invalid-feedback d-block">{validationErrors.resumen}</div>}
                        </div>

                        <button
                            type="button"
                            className="journal-filter-toggle"
                            onClick={() => setShowFilters(value => !value)}
                            aria-expanded={showFilters}
                            aria-controls="journal-advanced-filters"
                        >
                            <span className="d-flex align-items-center gap-2">
                                <i className={`bi bi-sliders${showFilters ? '2' : ''}`} aria-hidden="true"></i>
                                {t('journalFinder.search.advancedFilters')}
                                {activeFilterCount > 0 && <span className="journal-filter-count">{activeFilterCount}</span>}
                            </span>
                            <i className={`bi bi-chevron-${showFilters ? 'up' : 'down'}`} aria-hidden="true"></i>
                        </button>

                        <div id="journal-advanced-filters" className="journal-advanced-filters" hidden={!showFilters}>
                            <div className="journal-filter-block">
                                <fieldset>
                                    <legend>{t('journalFinder.filters.quartiles')}</legend>
                                    <div className="journal-choice-grid">
                                        {QUARTILES.map(quartile => (
                                            <button
                                                key={quartile}
                                                type="button"
                                                className={`journal-quartile-choice journal-quartile-choice--${quartile.toLowerCase()} ${formData.cuartiles.includes(quartile) ? 'is-selected' : ''}`}
                                                onClick={() => handleCuartilToggle(quartile)}
                                                aria-pressed={formData.cuartiles.includes(quartile)}
                                            >
                                                {quartile}
                                            </button>
                                        ))}
                                    </div>
                                    <small className="journal-filter-help">{t('journalFinder.search.noSelectionAllQuartiles')}</small>
                                </fieldset>
                            </div>

                            <div className="journal-filter-block journal-beall-control">
                                <div>
                                    <label htmlFor="excluir_beall" className="journal-check-label">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="excluir_beall"
                                            name="excluir_beall"
                                            checked={formData.excluir_beall}
                                            onChange={handleChange}
                                        />
                                        <span>
                                            <strong>{t('journalFinder.search.excludeBeallJournals')}</strong>
                                            <small>{t('journalFinder.search.beallLongTooltip')}</small>
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div className="journal-filter-block">
                                <fieldset>
                                    <legend>{t('journalFinder.filters.accessType')}</legend>
                                    <div className="journal-access-options">
                                        <label className={`journal-access-option ${formData.tipo_acceso === 'ambos' ? 'is-selected' : ''}`} htmlFor="acceso_ambos">
                                            <input type="radio" name="tipo_acceso" id="acceso_ambos" value="ambos" checked={formData.tipo_acceso === 'ambos'} onChange={handleChange} />
                                            <span>{t('journalFinder.filters.both')}</span>
                                        </label>
                                        <label className={`journal-access-option ${formData.tipo_acceso === 'abierto' ? 'is-selected' : ''}`} htmlFor="acceso_abierto">
                                            <input type="radio" name="tipo_acceso" id="acceso_abierto" value="abierto" checked={formData.tipo_acceso === 'abierto'} onChange={handleChange} />
                                            <span>{t('journalFinder.filters.openAccessShort')}</span>
                                        </label>
                                        <label className={`journal-access-option ${formData.tipo_acceso === 'cerrado' ? 'is-selected' : ''}`} htmlFor="acceso_cerrado">
                                            <input type="radio" name="tipo_acceso" id="acceso_cerrado" value="cerrado" checked={formData.tipo_acceso === 'cerrado'} onChange={handleChange} />
                                            <span>{t('journalFinder.filters.subscriptionShort')}</span>
                                        </label>
                                    </div>
                                </fieldset>
                            </div>

                            <div className="journal-filter-block journal-filter-grid">
                                <div>
                                    <label htmlFor="apc_preset" className="journal-filter-label">{t('journalFinder.search.maxCostApc')}</label>
                                    <select className="form-select journal-input" name="apc_preset" id="apc_preset" value={formData.apc_preset} onChange={handleChange}>
                                        {apcPresets.map(preset => <option key={preset.value} value={preset.value}>{preset.label}</option>)}
                                    </select>
                                </div>
                                {formData.apc_preset === 'custom' && (
                                    <div>
                                        <label htmlFor="apc_max" className="journal-filter-label">{t('journalFinder.filters.customApc')}</label>
                                        <input type="number" className="form-control journal-input" id="apc_max" name="apc_max" value={formData.apc_max ?? ''} onChange={handleChange} placeholder={t('journalFinder.search.customAmountPlaceholder')} min="0" step="100" />
                                    </div>
                                )}
                                <div>
                                    <label htmlFor="sjr_min" className="journal-filter-label">{t('journalFinder.search.sjrMinLabel')}</label>
                                    <input type="number" className="form-control journal-input" id="sjr_min" name="sjr_min" value={formData.sjr_min ?? ''} onChange={handleChange} placeholder={t('journalFinder.search.sjrMinPlaceholder')} min="0" step="0.1" />
                                    <small className="journal-filter-help">{t('journalFinder.search.sjrHint')}</small>
                                </div>
                            </div>

                            <div className="journal-filter-block">
                                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                                    <span className="journal-filter-label mb-0">{t('journalFinder.filters.thematicAreas')}</span>
                                    {areasTematicas.length > 0 && <span className="journal-filter-selection">{formData.areas_tematicas.length}/{areasTematicas.length}</span>}
                                </div>
                                {areasLoading && <div className="journal-inline-status" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true"></span>{t('journalFinder.filters.loadingAreas')}</div>}
                                {areasError && <div className="journal-inline-error" role="alert">{areasError}</div>}
                                {!areasLoading && !areasError && areasTematicas.length > 0 && (
                                    <div className="journal-area-list">
                                        {areasTematicas.map(area => (
                                            <label key={area.id} className="journal-area-option" htmlFor={`area_${area.id}`}>
                                                <input type="checkbox" className="form-check-input" id={`area_${area.id}`} checked={formData.areas_tematicas.includes(area.id)} onChange={() => handleAreaToggle(area.id)} />
                                                <span>{area.nombre}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                {!areasLoading && !areasError && !areasTematicas.length && <small className="journal-filter-help">{t('journalFinder.filters.noAreas')}</small>}
                                <small className="journal-filter-help">{t('journalFinder.search.noSelectionAllAreas')}</small>
                            </div>
                        </div>

                        {validationErrors.form && <div className="journal-form-error" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true"></i>{validationErrors.form}</div>}

                        <div className="journal-form-actions">
                            <button type="submit" className="btn journal-primary-action" disabled={busy || !hasInput}>
                                {loading ? <><span className="spinner-border spinner-border-sm" aria-hidden="true"></span>{t('journalFinder.form.searching')}</> : <><i className="bi bi-search" aria-hidden="true"></i>{t('journalFinder.search.searchTitle')}</>}
                            </button>
                            <button type="button" className="btn journal-secondary-action" onClick={handleReset} disabled={busy}>
                                <i className="bi bi-arrow-counterclockwise" aria-hidden="true"></i>{t('journalFinder.search.clearButton')}
                            </button>
                        </div>
                    </form>
                </aside>

                <section className="journal-results-panel" aria-labelledby="journal-results-title" ref={resultsSectionRef} tabIndex="-1">
                    <div className="journal-results-header">
                        <div>
                            <p className="journal-section-label">{t('journalFinder.results.sectionLabel')}</p>
                            <h2 id="journal-results-title" ref={resultsHeadingRef} tabIndex="-1">
                                {t('journalFinder.results.title')}
                            </h2>
                            <p className="journal-results-subtitle">{hasSearched ? t('journalFinder.results.resultsIntro') : t('journalFinder.results.emptyIntro')}</p>
                        </div>
                        {resultados.length > 0 && (
                            <div className="journal-results-total" aria-live="polite">
                                <strong>{resultados.length}</strong>
                                <span>{t('journalFinder.results.showingLabel')}</span>
                            </div>
                        )}
                    </div>

                    {activeFilterCount > 0 && (
                        <div className="journal-active-filters">
                            <span className="journal-active-label">{t('journalFinder.filters.activeFilters')}</span>
                            {formData.cuartiles.length > 0 && <span className="journal-filter-chip">{formData.cuartiles.join(', ')}</span>}
                            {formData.tipo_acceso !== 'ambos' && <span className="journal-filter-chip">{formData.tipo_acceso === 'abierto' ? t('journalFinder.filters.openAccessShort') : t('journalFinder.filters.subscriptionShort')}</span>}
                            {formData.apc_max !== null && <span className="journal-filter-chip">APC ≤ {formData.apc_max === 0 ? '0' : `$${formData.apc_max}`}</span>}
                            {formData.sjr_min !== null && formData.sjr_min !== '' && <span className="journal-filter-chip">SJR ≥ {formData.sjr_min}</span>}
                            {formData.areas_tematicas.length > 0 && <span className="journal-filter-chip">{formData.areas_tematicas.length} {t('journalFinder.filters.thematicAreas').toLowerCase()}</span>}
                        </div>
                    )}

                    {resultados.length > 0 && !loading && (
                        <div className="journal-sort-toolbar">
                            <label htmlFor="journal-order" className="journal-sort-label"><i className="bi bi-sort-down" aria-hidden="true"></i>{t('journalFinder.sorting.sortBy')}</label>
                            <select id="journal-order" className="form-select journal-sort-select" value={orderBy} onChange={event => handleOrderChange(event.target.value)} disabled={busy}>
                                <option value="sjr">{t('journalFinder.sorting.sjrLabel')}</option>
                                <option value="h_index">{t('journalFinder.sorting.hIndexLabel')}</option>
                                <option value="produccion_ultimo_anio">{t('journalFinder.sorting.lastYearPublications')}</option>
                            </select>
                            <div className="journal-sort-direction">
                                <button type="button" className={orderDir === 'desc' ? 'is-selected' : ''} onClick={() => handleOrderChange(orderBy, 'desc')} disabled={busy || orderDir === 'desc'} aria-pressed={orderDir === 'desc'}>{t('journalFinder.sorting.highToLow')}</button>
                                <button type="button" className={orderDir === 'asc' ? 'is-selected' : ''} onClick={() => handleOrderChange(orderBy, 'asc')} disabled={busy || orderDir === 'asc'} aria-pressed={orderDir === 'asc'}>{t('journalFinder.sorting.lowToHigh')}</button>
                            </div>
                        </div>
                    )}

                    {requestMessage && hasSearched && !loading && <div className="journal-request-message" role="status"><i className="bi bi-info-circle" aria-hidden="true"></i><span>{requestMessage}</span></div>}

                    {error && (
                        <div className="journal-error-state" role="alert">
                            <div className="journal-state-icon journal-state-icon--error"><i className="bi bi-exclamation-triangle" aria-hidden="true"></i></div>
                            <div>
                                <h3>{t('journalFinder.errors.searchTitle')}</h3>
                                <p>{error}</p>
                                <button type="button" className="btn btn-sm journal-retry-button" onClick={() => handleSubmit(null)} disabled={busy}>{t('journalFinder.errors.retry')}</button>
                            </div>
                        </div>
                    )}

                    {loading && !loadingMore && (
                        <div className="journal-skeleton-list" role="status" aria-busy="true">
                            <span className="visually-hidden">{t('journalFinder.results.loadingInitial')}</span>
                            {[1, 2, 3].map(item => <div className="journal-skeleton" key={item}><span></span><div><b></b><i></i><em></em></div></div>)}
                        </div>
                    )}

                    {!hasSearched && !loading && !error && (
                        <div className="journal-empty-state">
                            <div className="journal-empty-state__visual" aria-hidden="true"><i className="bi bi-search"></i><span></span><span></span></div>
                            <h3>{t('journalFinder.results.emptyTitle')}</h3>
                            <p>{t('journalFinder.results.emptyState')}</p>
                        </div>
                    )}

                    {hasSearched && !loading && !error && resultados.length === 0 && (
                        <div className="journal-empty-state journal-empty-state--no-results">
                            <div className="journal-state-icon" aria-hidden="true"><i className="bi bi-search"></i></div>
                            <h3>{t('journalFinder.results.noResultsTitle')}</h3>
                            <p>{requestMessage || t('journalFinder.results.noResults')}</p>
                            <button type="button" className="btn btn-sm journal-retry-button" onClick={() => setShowFilters(true)}>{t('journalFinder.results.adjustFilters')}</button>
                        </div>
                    )}

                    {resultados.length > 0 && (
                        <div className="journal-result-list" aria-live="polite">
                            {resultados.map((revista, index) => {
                                const resultId = `journal-result-${revista.id || index}`;
                                const isExpanded = expandedResultId === revista.id;
                                return (
                                    <article className="journal-result-card" key={revista.id || index}>
                                        <div className="journal-result-main">
                                            <span className="journal-result-rank">{String(index + 1).padStart(2, '0')}</span>
                                            <div className="journal-result-title-wrap">
                                                <div className="journal-result-title-line">
                                                    <h3>{revista.nombre}</h3>
                                                    {revista.cuartil && <span className={`journal-quartile-badge journal-quartile-badge--${revista.cuartil.toLowerCase()}`}>{revista.cuartil}</span>}
                                                </div>
                                                {revista.razon_posicion && index < 5 && <p className="journal-result-reason"><i className="bi bi-lightbulb" aria-hidden="true"></i>{revista.razon_posicion}</p>}
                                                <div className="journal-result-meta">
                                                    {revista.editorial && <span><i className="bi bi-building" aria-hidden="true"></i>{revista.editorial}</span>}
                                                    {revista.issn && <span><i className="bi bi-hash" aria-hidden="true"></i>{t('journalFinder.card.issn')}: {revista.issn}</span>}
                                                    {revista.url_homepage && <a href={revista.url_homepage} target="_blank" rel="noopener noreferrer"><i className="bi bi-box-arrow-up-right" aria-hidden="true"></i>{t('journalFinder.card.visitWebsite')}</a>}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="journal-metrics-row">
                                            {revista.sjr !== null && revista.sjr !== undefined && <div className={`journal-metric ${orderBy === 'sjr' ? 'is-primary' : ''}`}><span>{t('journalFinder.card.sjr')}</span><strong>{Number(revista.sjr).toFixed(3)}</strong></div>}
                                            {revista.indice_h !== null && revista.indice_h !== undefined && <div className={`journal-metric ${orderBy === 'h_index' ? 'is-primary' : ''}`}><span>{t('journalFinder.card.hIndex')}</span><strong>{revista.indice_h}</strong></div>}
                                            {revista.produccion_ultimo_anio && <div className={`journal-metric ${orderBy === 'produccion_ultimo_anio' ? 'is-primary' : ''}`}><span>{t('journalFinder.card.productionLastYear')}</span><strong>{revista.produccion_ultimo_anio.split(' ')[0]}</strong></div>}
                                            {revista.produccion_ultimos_3 && <div className="journal-metric"><span>{t('journalFinder.card.productionLast3Years')}</span><strong>{revista.produccion_ultimos_3.split(' ')[0]}</strong></div>}
                                        </div>

                                        <div className="journal-result-trust">
                                            {isYes(revista.acceso_abierto) && <span className="journal-trust-badge journal-trust-badge--positive"><i className="bi bi-unlock" aria-hidden="true"></i>{t('journalFinder.card.openAccess')}</span>}
                                            {isYes(revista.es_medline) && <span className="journal-trust-badge journal-trust-badge--positive"><i className="bi bi-patch-check" aria-hidden="true"></i>{t('journalFinder.card.indexedMedline')}</span>}
                                            {isYes(revista.en_lista_beall) && <span className="journal-trust-badge journal-trust-badge--warning"><i className="bi bi-exclamation-triangle" aria-hidden="true"></i>{t('journalFinder.card.beallList')}</span>}
                                        </div>

                                        <button type="button" className="journal-result-toggle" onClick={() => setExpandedResultId(isExpanded ? null : revista.id)} aria-expanded={isExpanded} aria-controls={resultId}>
                                            {isExpanded ? t('journalFinder.card.hideDetails') : t('journalFinder.card.showDetails')}
                                            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} aria-hidden="true"></i>
                                        </button>

                                        <div id={resultId} className={`journal-result-details ${isExpanded ? 'is-expanded' : ''}`}>
                                            {revista.areas && <div><span>{t('journalFinder.card.thematicAreas')}</span><p>{revista.areas.split(';').slice(0, 4).map(area => area.trim()).join(' · ')}</p></div>}
                                            {revista.categorias && <div><span>{t('journalFinder.card.asjcCategories')}</span><p>{revista.categorias.split(';').slice(0, 4).map(category => category.trim()).join(' · ')}</p></div>}
                                            {revista.apc && <div><span>{t('journalFinder.card.apc')}</span><p>{formatApc(revista.apc)}</p></div>}
                                            {revista.frecuencia && <div><span>{t('journalFinder.card.frequency')}</span><p>{revista.frecuencia}</p></div>}
                                            {revista.tipo_revision && <div><span>{t('journalFinder.card.reviewType')}</span><p>{revista.tipo_revision}</p></div>}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}

                    {loadingMore && <div className="journal-load-more-status" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true"></span>{t('journalFinder.results.loadingMore')}</div>}

                    {resultados.length > 0 && !loading && !loadingMore && (
                        <div className="journal-results-footer">
                            {hasMore ? (
                                <button type="button" className="btn journal-load-more-button" onClick={() => handleSubmit(null, true)} disabled={busy}>{t('journalFinder.results.loadMoreButton')}<i className="bi bi-arrow-down" aria-hidden="true"></i></button>
                            ) : <span><i className="bi bi-check2-circle" aria-hidden="true"></i>{t('journalFinder.results.noMoreResults')}</span>}
                        </div>
                    )}
                </section>
            </div>
        </main>
    );
};

export default JournalFinderPage;
